import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Search, Calendar, X, CheckSquare, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { CATEGORIES, categoryColor, categoryLabel, priorityClasses, formatRelative, formatDateTime, stripMentionIds } from "../lib/constants";
import { useConfirm } from "../components/Confirm";

export default function StreamHistory() {
  const confirm = useConfirm();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [pageSize] = useState(50);
  const [messageType, setMessageType] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [creatingTaskFrom, setCreatingTaskFrom] = useState(null);
  const [taskDraft, setTaskDraft] = useState({ title: "", priority: "medium" });
  const [creatingTask, setCreatingTask] = useState(false);

  const search = async (searchOffset = 0) => {
    setLoading(true);
    try {
      const params = {
        limit: pageSize,
        offset: searchOffset,
      };
      if (searchQuery.trim()) params.q = searchQuery.trim();
      if (fromDate) params.from_date = new Date(fromDate).toISOString();
      if (toDate) params.to_date = new Date(toDate).toISOString();

      const res = await api.get("/messages/search", { params });
      setMessages(res.data.messages || []);
      setTotal(res.data.total || 0);
      setOffset(searchOffset);
      setHasMore(res.data.has_more || false);
    } catch {
      toast.error("Failed to search messages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    search(0);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    search(0);
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setFromDate("");
    setToDate("");
    setMessageType("all");
    setFilterCategory("all");
    setSelectedIds(new Set());
    setOffset(0);
    search(0);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredMessages.length && filteredMessages.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredMessages.map((m) => m.id)));
    }
  };

  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const ok = await confirm({
      title: `Delete ${selectedIds.size} message${selectedIds.size > 1 ? "s" : ""}?`,
      message: "This cannot be undone.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!ok) return;
    setBulkDeleting(true);
    try {
      await Promise.all([...selectedIds].map((id) => api.delete(`/messages/${id}`)));
      setMessages((prev) => prev.filter((m) => !selectedIds.has(m.id)));
      setTotal((prev) => prev - selectedIds.size);
      setSelectedIds(new Set());
      toast.success(`${selectedIds.size} messages deleted`);
    } catch {
      toast.error("Some messages could not be deleted");
    } finally {
      setBulkDeleting(false);
    }
  };

  const getTypeCounts = () => {
    const all = messages.length;
    const group = messages.filter((m) => m.chat_type === "group").length;
    const individual = messages.filter((m) => m.chat_type === "individual").length;
    return { all, group, individual };
  };

  const filteredMessages = messages.filter((m) => {
    if (messageType === "group" && m.chat_type !== "group") return false;
    if (messageType === "individual" && m.chat_type !== "individual") return false;
    if (filterCategory !== "all" && m.classification?.category !== filterCategory) return false;
    return true;
  });

  const typeCounts = getTypeCounts();

  const categoryCounts = (() => {
    const base = messages.filter((m) => {
      if (messageType === "group" && m.chat_type !== "group") return false;
      if (messageType === "individual" && m.chat_type !== "individual") return false;
      return true;
    });
    const counts = { all: base.length };
    CATEGORIES.forEach((c) => { counts[c.key] = base.filter((m) => m.classification?.category === c.key).length; });
    return counts;
  })();

  const handleLoadMore = () => {
    search(offset + pageSize);
  };

  const deleteMessage = async (messageId) => {
    const ok = await confirm({
      title: "Delete this message?",
      message: "This cannot be undone.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/messages/${messageId}`);
      setMessages((prev) => prev.filter((m) => m.id !== messageId));
      setTotal((prev) => prev - 1);
      toast.success("Message deleted");
    } catch {
      toast.error("Failed to delete message");
    }
  };

  const createTaskFromMessage = async (message) => {
    if (!taskDraft.title.trim()) {
      toast.error("Task title is required");
      return;
    }
    setCreatingTask(true);
    try {
      await api.post("/tasks/create", {
        title: taskDraft.title,
        category: "task",
        priority: taskDraft.priority,
        status: "pending",
        deadline: null,
        notes: `Created from message: ${message.chat_name} › ${message.sender_name}\n\nOriginal message:\n${message.text}`,
      });
      toast.success("Task created");
      setCreatingTaskFrom(null);
      setTaskDraft({ title: "", priority: "medium" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create task");
    } finally {
      setCreatingTask(false);
    }
  };

  const formatRecipient = (chatName) => {
    const s = (chatName || "").trim();
    return /^\+?\d{7,}$/.test(s) ? `+${s.replace(/^\+/, "")}` : s;
  };

  const getSenderDisplay = (m) => {
    if (m.chat_type === "group") {
      return m.direction === "outgoing"
        ? `${m.chat_name} (you sent)`
        : `${m.chat_name} › ${m.sender_name}`;
    }
    if (m.direction === "outgoing") {
      return `Sent to ${formatRecipient(m.chat_name)}`;
    }
    return m.sender_name;
  };

  const getDirectionBadge = (direction) => {
    if (direction === "outgoing") {
      return "border-slate-300 text-slate-500 bg-slate-50";
    }
    return "border-slate-900 text-slate-900 bg-white";
  };

  return (
    <div className="p-6 md:p-8 max-w-[1400px] mx-auto space-y-6" data-testid="stream-history-page">
      {/* Header */}
      <div>
        <h1 className="text-[32px] font-extrabold tracking-tight text-slate-900 leading-tight">Stream History</h1>
        <p className="text-[14px] text-slate-500 mt-1">Search and browse all messages by text, sender, or date range.</p>
      </div>

      {/* Search and Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {/* Search Input */}
          <div className="md:col-span-3">
            <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-2 block">Search Messages</label>
            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="flex-1 relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by message content, sender name, or chat name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-10 pl-10 pr-3 border border-slate-200 rounded-xl text-[13.5px] focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 outline-none transition"
                  data-testid="search-input"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="h-10 px-5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-[13px] font-semibold disabled:opacity-50 transition active:scale-95"
              >
                Search
              </button>
            </form>
          </div>

          {/* Date Range */}
          <div>
            <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-2 flex items-center gap-1">
              <Calendar size={11} /> From Date
            </label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full h-10 px-3 border border-slate-200 rounded-xl text-[13px] focus:border-blue-600 outline-none transition"
              data-testid="from-date"
            />
          </div>

          <div>
            <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-2 flex items-center gap-1">
              <Calendar size={11} /> To Date
            </label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-full h-10 px-3 border border-slate-200 rounded-xl text-[13px] focus:border-blue-600 outline-none transition"
              data-testid="to-date"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={handleClearFilters}
              className="w-full h-10 border border-slate-200 hover:border-slate-900 rounded-xl text-[13px] font-medium transition flex items-center justify-center gap-2"
            >
              <X size={14} />
              Clear Filters
            </button>
          </div>
        </div>

        {/* Message Type Filter */}
        <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-slate-100">
          <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400 shrink-0">Source:</span>
          {["all", "group", "individual"].map((type) => {
            const count = type === "all" ? typeCounts.all : type === "group" ? typeCounts.group : typeCounts.individual;
            const label = type === "all" ? "All" : type === "group" ? "Groups" : "Personal";
            return (
              <button
                key={type}
                onClick={() => { setMessageType(type); setSelectedIds(new Set()); }}
                className={`px-3 py-1.5 rounded-full text-[12px] font-semibold transition border ${
                  messageType === type
                    ? "bg-blue-600 text-white border-blue-600"
                    : "border-slate-200 text-slate-600 hover:border-slate-900 hover:text-slate-900"
                }`}
                data-testid={`filter-type-${type}`}
              >
                {label} ({count})
              </button>
            );
          })}
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-slate-100">
          <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400 shrink-0">Category:</span>
          <button
            onClick={() => { setFilterCategory("all"); setSelectedIds(new Set()); }}
            className={`px-3 py-1.5 rounded-full text-[12px] font-semibold transition border ${
              filterCategory === "all" ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 hover:border-slate-900 hover:text-slate-900"
            }`}
          >
            All ({categoryCounts.all})
          </button>
          {CATEGORIES.filter((c) => categoryCounts[c.key] > 0).map((c) => (
            <button
              key={c.key}
              onClick={() => { setFilterCategory(c.key); setSelectedIds(new Set()); }}
              className={`px-3 py-1.5 rounded-full text-[12px] font-semibold transition border ${
                filterCategory === c.key ? "text-white border-transparent" : "border-slate-200 text-slate-600 hover:border-slate-900 hover:text-slate-900"
              }`}
              style={filterCategory === c.key ? { backgroundColor: c.color, borderColor: c.color } : {}}
            >
              {c.label} ({categoryCounts[c.key]})
            </button>
          ))}
        </div>

        {/* Active Filters Summary */}
        {(searchQuery || fromDate || toDate || messageType !== "all") && (
          <div className="text-[12px] text-slate-600 pt-2 border-t border-slate-100 flex flex-wrap gap-2 items-center">
            <span className="font-semibold text-slate-500">Active filters:</span>
            {searchQuery && <span className="px-2 py-1 bg-blue-50 border border-blue-200 rounded-lg text-blue-700">"{searchQuery}"</span>}
            {fromDate && <span className="px-2 py-1 bg-slate-100 rounded-lg text-slate-700">From: {formatDateTime(new Date(fromDate).toISOString())}</span>}
            {toDate && <span className="px-2 py-1 bg-slate-100 rounded-lg text-slate-700">To: {formatDateTime(new Date(toDate).toISOString())}</span>}
            {messageType !== "all" && <span className="px-2 py-1 bg-slate-100 rounded-lg text-slate-700">{messageType === "group" ? "Group" : "Individual"}</span>}
          </div>
        )}
      </div>

      {/* Results */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {/* Header */}
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50/50">
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer" title="Select all visible messages">
              <input
                type="checkbox"
                checked={filteredMessages.length > 0 && selectedIds.size === filteredMessages.length}
                ref={(el) => { if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < filteredMessages.length; }}
                onChange={toggleSelectAll}
                className="w-4 h-4 rounded border-slate-300 accent-blue-600 cursor-pointer"
              />
            </label>
            {selectedIds.size > 0 ? (
              <button
                onClick={bulkDelete}
                disabled={bulkDeleting}
                className="h-7 px-3 inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-[12px] font-semibold transition active:scale-95 disabled:opacity-60"
              >
                <Trash2 size={12} />
                {bulkDeleting ? "Deleting…" : `Delete ${selectedIds.size}`}
              </button>
            ) : (
              <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-700">
                <span className="material-symbols-outlined text-[16px]" style={{ fontVariationSettings: "'FILL' 1" }}>history</span>
                Results
              </div>
            )}
          </div>
          <div className="text-[12px] text-slate-500">
            {loading ? "Loading…" : `${filteredMessages.length} of ${total} total messages`}
          </div>
        </div>

        {/* Messages List */}
        <div className="max-h-[calc(100vh-400px)] overflow-y-auto">
          {loading && messages.length === 0 && (
            <div className="px-5 py-10 text-center text-slate-500 text-[13px]">Loading messages…</div>
          )}
          {!loading && filteredMessages.length === 0 && (
            <div className="px-5 py-12 text-center text-slate-500 text-[13px]">
              {total === 0 ? "No messages found. Try different search terms or date range." : "No messages match this filter."}
            </div>
          )}

          {filteredMessages.map((m) => (
            <div
              key={m.id}
              className={"relative pl-12 pr-5 py-4 border-b last:border-b-0 border-slate-100 transition group " +
                (selectedIds.has(m.id) ? "bg-blue-50/60" : "hover:bg-slate-50/60")}
              data-testid={`history-msg-${m.id}`}
            >
              <div className={"absolute left-0 top-0 bottom-0 w-1 " +
                (m.is_status ? "bg-slate-300" : m.direction === "outgoing" ? "bg-slate-400" : "bg-blue-500")} />
              <div className="absolute left-4 top-1/2 -translate-y-1/2">
                <input
                  type="checkbox"
                  checked={selectedIds.has(m.id)}
                  onChange={() => toggleSelect(m.id)}
                  onClick={(e) => e.stopPropagation()}
                  className="w-4 h-4 rounded border-slate-300 accent-blue-600 cursor-pointer opacity-0 group-hover:opacity-100 transition"
                  style={selectedIds.has(m.id) ? { opacity: 1 } : {}}
                />
              </div>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={"px-2 py-0.5 text-[9.5px] tracking-[0.16em] uppercase font-bold rounded-full border " +
                      (m.direction === "outgoing" ? "border-slate-300 text-slate-500 bg-slate-50" : "border-blue-200 text-blue-700 bg-blue-50")}>
                      {m.is_status ? "Status" : m.direction}
                    </span>
                    {m.chat_type === "group" ? (
                      <span className="material-symbols-outlined text-[14px] text-slate-400">group</span>
                    ) : (
                      <span className="material-symbols-outlined text-[14px] text-slate-400">person</span>
                    )}
                    <div className="text-[13px] font-semibold text-slate-900 truncate">{getSenderDisplay(m)}</div>
                  </div>
                  {m.sender_phone && m.chat_type === "individual" && m.direction !== "outgoing" && (
                    <div className="text-[11px] text-slate-500 mt-1">{m.sender_phone}</div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <div className="text-[11px] text-slate-400 whitespace-nowrap">{formatRelative(m.timestamp || m.created_at)}</div>
                  <button
                    onClick={() => deleteMessage(m.id)}
                    className="opacity-0 group-hover:opacity-100 h-7 w-7 grid place-items-center text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                    title="Delete message"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>

              <div className="text-[13.5px] text-slate-700 mt-2 whitespace-pre-wrap bg-slate-50 p-3 rounded-lg border border-slate-100">
                {stripMentionIds(m.text)}
              </div>

              {m.classification && !m.is_status && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span
                    className="text-[10px] tracking-[0.18em] uppercase font-bold px-2 py-1 border rounded"
                    style={{ borderColor: categoryColor(m.classification.category), color: categoryColor(m.classification.category), background: categoryColor(m.classification.category) + "12" }}
                  >
                    {categoryLabel(m.classification.category)}
                  </span>
                  {m.classification.priority && (
                    <span className={"text-[10px] tracking-[0.18em] uppercase font-bold px-2 py-1 border rounded " + priorityClasses(m.classification.priority)}>
                      {m.classification.priority}
                    </span>
                  )}
                  {m.classification.confidence && (
                    <span className="text-[11px] text-slate-400">
                      {Math.round((m.classification.confidence || 0) * 100)}% conf
                    </span>
                  )}
                  {m.classification.summary && (
                    <span className="text-[12px] text-slate-600 italic basis-full mt-1">"{stripMentionIds(m.classification.summary)}"</span>
                  )}
                </div>
              )}

              <div className="mt-3 flex justify-end">
                <button
                  onClick={() => {
                    setCreatingTaskFrom(m);
                    setTaskDraft({ title: m.text.slice(0, 60), priority: "medium" });
                  }}
                  className="h-8 px-4 inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-[12px] font-semibold transition active:scale-95"
                >
                  <CheckSquare size={13} />
                  Create Task
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {!loading && hasMore && (
          <div className="px-5 py-4 border-t border-slate-200 text-center">
            <button
              onClick={handleLoadMore}
              disabled={loading}
              className="h-9 px-5 border border-slate-200 hover:border-slate-900 rounded-xl text-[13px] font-medium transition"
            >
              Load More
            </button>
          </div>
        )}
      </div>

      {/* Stats */}
      {!loading && filteredMessages.length > 0 && (
        <div className="text-[12px] text-slate-500 text-center">
          Showing {offset + filteredMessages.length} of {total} messages
        </div>
      )}

      {/* Create Task Modal */}
      {creatingTaskFrom && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white border border-slate-200 rounded-xl shadow-xl w-full max-w-2xl">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-[17px] font-bold text-slate-900">Create Task from Message</h2>
              <button
                onClick={() => setCreatingTaskFrom(null)}
                className="h-8 w-8 grid place-items-center border border-slate-200 hover:border-slate-900 rounded-lg transition"
              >
                <X size={16} />
              </button>
            </div>

            <div className="px-6 py-6 space-y-4">
              <div>
                <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-2 block">From Message</label>
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-[13px] text-slate-700">
                  <div className="font-semibold mb-1">{getSenderDisplay(creatingTaskFrom)}</div>
                  <div className="text-[12px] text-slate-600 line-clamp-2">{stripMentionIds(creatingTaskFrom.text)}</div>
                </div>
              </div>

              <div>
                <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-2 block">Task Title</label>
                <input
                  type="text"
                  value={taskDraft.title}
                  onChange={(e) => setTaskDraft({ ...taskDraft, title: e.target.value })}
                  placeholder="Enter task title..."
                  className="w-full h-10 px-3 border border-slate-200 rounded-lg text-[13.5px] focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 outline-none transition"
                  autoFocus
                />
              </div>

              <div>
                <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-2 block">Priority</label>
                <select
                  value={taskDraft.priority}
                  onChange={(e) => setTaskDraft({ ...taskDraft, priority: e.target.value })}
                  className="w-full h-10 px-2 border border-slate-200 rounded-lg text-[13.5px] focus:border-blue-600 outline-none bg-white transition"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              <div className="flex gap-2 pt-4 border-t border-slate-200">
                <button
                  onClick={() => createTaskFromMessage(creatingTaskFrom)}
                  disabled={creatingTask}
                  className="flex-1 h-10 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-[14px] font-semibold transition active:scale-95 disabled:opacity-60"
                >
                  {creatingTask ? "Creating…" : "Create Task"}
                </button>
                <button
                  onClick={() => setCreatingTaskFrom(null)}
                  className="flex-1 h-10 border border-slate-200 hover:border-slate-900 rounded-xl text-[14px] transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
